import React, { useState, useEffect } from 'react';
import api from '../api';

const Plans = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const res = await api.get('/api/settings/public');
            if (res.data.success && res.data.data && res.data.data.plans) {
                setPlans(res.data.data.plans);
            } else {
                console.warn("Plans data missing in response:", res.data);
                setError("Information about subscription plans is temporarily unavailable.");
            }
        } catch (err) {
            console.error("Error fetching plans:", err);
            const errorMsg = err.response?.data?.error || err.message;
            setError(`Could not connect to the security services. (${errorMsg})`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="plans-page py-5 bg-light min-vh-100">
            <div className="container py-5">
                {/* Header Section */}
                <div className="text-center mb-5">
                    <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill fw-bold mb-3">Pricing & Plans</span>
                    <h1 className="display-4 fw-bold mb-3">Simple, Transparent Pricing</h1>
                    <p className="text-muted fs-5 mx-auto mb-4 max-w-[700px]">
                        Choose the right plan for your business needs.
                        Join our mission to democratize enterprise-grade security.
                    </p>

                    {/* Free Beta Badge */}
                    <div className="alert alert-primary d-inline-block border-0 shadow-sm rounded-4 px-4 py-3 mb-4">
                        <div className="d-flex align-items-center">
                            <i className="las la-gift fs-1 me-3 text-primary animate-bounce"></i>
                            <div className="text-start">
                                <h5 className="fw-bold mb-1">Public Beta Special</h5>
                                <p className="mb-0 small">All premium features are currently <strong>FREE</strong> for early adopters!</p>
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger text-center shadow-sm border-0 rounded-4">
                        <i className="las la-exclamation-triangle fs-3 mb-3 d-block"></i>
                        {error}
                    </div>
                ) : (
                    <div className="row g-4 justify-content-center">
                        {plans.map((plan) => (
                            <div className="col-lg-4 col-md-6" key={plan.id}>
                                <div className={`card border-0 h-100 rounded-5 shadow-sm overflow-hidden position-relative ${plan.highlight ? 'ring-primary' : ''}`}>
                                    {plan.highlight && (
                                        <div className="bg-primary text-white text-center py-2 fw-bold small position-absolute top-0 w-100">
                                            MOST POPULAR
                                        </div>
                                    )}
                                    <div className={`card-body p-5 ${plan.highlight ? 'pt-5' : ''}`}>
                                        <div className="mb-4">
                                            <h4 className="fw-bold mb-2">{plan.name}</h4>
                                            <div className="d-flex align-items-baseline mb-3">
                                                <span className="h1 fw-bold mb-0">${plan.price}</span>
                                                <span className="text-muted ms-2">/{plan.interval}</span>
                                            </div>
                                            <p className="text-muted small">Planned commercial price after beta.</p>
                                        </div>

                                        <hr className="my-4 opacity-10" />

                                        <h6 className="fw-bold mb-4">What's included:</h6>
                                        <ul className="list-unstyled mb-5">
                                            {plan.features.map((feature, idx) => (
                                                <li className="mb-3 d-flex align-items-center" key={idx}>
                                                    <i className="las la-check-circle text-primary fs-4 me-2"></i>
                                                    <span className="small">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        <div className="mt-auto">
                                            <div className="btn btn-outline-primary w-100 py-3 rounded-pill fw-bold disabled">
                                                Coming Soon in Beta
                                            </div>
                                            <p className="text-center text-muted x-small mt-3 mb-0">
                                                Sign up now to lock in early adopter status.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* FAQ or Info Section */}
                <div className="mt-5 pt-5 text-center">
                    <h3 className="fw-bold mb-4">Enterprise Customization?</h3>
                    <p className="text-muted mb-4">Need a custom plan for your large organization? We're happy to talk.</p>
                    <a href="mailto:support@secure360.us" className="btn btn-dark btn-lg px-5 rounded-pill shadow-sm">
                        Contact Sales
                    </a>
                </div>
            </div>

            <style>{`
                .ring-primary {
                    box-shadow: 0 0 0 3px #0d6efd !important;
                }
                .animate-bounce {
                    animation: bounce 2s infinite;
                }
                @keyframes bounce {
                    0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
                    40% {transform: translateY(-10px);}
                    60% {transform: translateY(-5px);}
                }
                .x-small { font-size: 0.75rem; }
            `}</style>
        </div>
    );
};

export default Plans;
